import dotenv from "@dotenvx/dotenvx";
import { createServerOnlyFn } from "@tanstack/react-start";

export type PublicConfig = {
  env: string | undefined;
  api: {
    rest: string | undefined;
  };
  // True for any real deployed environment (staging, production) — false
  // only for local dev. Not a literal production check: gates things like
  // Sentry init and hiding the TanStack devtools panel, both of which
  // should also be on/off correctly on staging, not just flip once this
  // actually reaches production (same reasoning as apps/api's own
  // isDeployed field).
  isDeployed: boolean;
  sentry: {
    dsn: string | undefined;
    releaseVersion: string | undefined;
  };
  domain: string;
  host: string;
  google: {
    tag_id: string | undefined;
  };
  ipinfo: {
    token: string | undefined;
  };
  editorUrl: string;
};

declare global {
  interface Window {
    // Set by an inline script RootDocument renders ahead of <Scripts/>
    // (document order guarantees it runs before this module is evaluated
    // on the client) — see RootDocument.tsx.
    __FLOWSTEP_CONFIG__?: PublicConfig;
  }
}

function fromProcessEnv(): PublicConfig {
  return {
    env: process.env.VITE_PUBLIC_ENV,
    api: {
      rest: process.env.VITE_PUBLIC_REST_ENDPOINT,
    },
    isDeployed: ["staging", "production"].includes(
      process.env.VITE_PUBLIC_ENV || "development",
    ),
    sentry: {
      dsn: process.env.VITE_PUBLIC_SENTRY_DSN,
      releaseVersion: process.env.VITE_PUBLIC_SENTRY_RELEASE,
    },
    domain: process.env.VITE_PUBLIC_DOMAIN ?? "flowstep.ai",
    host: process.env.VITE_PUBLIC_HOST ?? "flowstep.ai",
    google: {
      tag_id: process.env.VITE_PUBLIC_GOOGLE_TAG,
    },
    ipinfo: {
      token: process.env.VITE_PUBLIC_IPINFO_TOKEN,
    },
    editorUrl: process.env.VITE_EDITOR_URL ?? "https://app.flowstep.ai",
  };
}

// Previously decrypted at BUILD time (apps/copilot/package.json's `_dx`
// wrapper), baking these values into the bundle via Vite's static
// `import.meta.env` replacement — one bundle per environment. Moved to
// runtime (same dotenvx mechanism as apps/api's config.ts) so one image
// built per commit SHA can serve every stack via Pulumi's `imageTag`,
// instead of needing a separate build per environment. See
// infra/copilot-service/secrets.ts for the key this decrypts with.
//
// Client: window.__FLOWSTEP_CONFIG__ already holds the server-resolved
// values by the time this module is evaluated (see the Window global
// above), so the client branch never touches process.env at all.
// createServerOnlyFn, not a plain function + typeof-window check: a
// runtime check alone doesn't stop the @dotenvx/dotenvx import itself from
// being bundled into the CLIENT output too — confirmed by inspecting a
// real client build, which included dotenvx's full CJS internals
// (including an os.homedir() call that doesn't exist in the browser,
// crashing with "r.homedir is not a function" and silently breaking
// hydration downstream). createServerOnlyFn is a build-time macro (its
// runtime body is just `(fn) => fn`) that the TanStack Start Vite plugin
// recognizes and strips for the client bundle specifically, taking the
// now-dead dotenvx import down with it.
const fromDecryptedEnvFile = createServerOnlyFn((): PublicConfig => {
  // Not `convention: "nextjs"` — confirmed by local repro (with the real
  // staging key) that it does NOT give .env.{DEPLOY_ENV} precedence over
  // the base .env for overlapping keys, so every VITE_PUBLIC_*/
  // VITE_EDITOR_URL value silently stayed on .env's dev defaults
  // (localhost, "development") even with the right stack correctly set.
  // An explicit `path` array, .env then .env.{DEPLOY_ENV}, combined with
  // `overload: true` so the later file in that list actually overrides
  // the first, is what verified correctly in that same repro.
  //
  // DEPLOY_ENV, not NODE_ENV: confirmed by direct inspection of the
  // compiled output that Vite's esbuild `define` step constant-folds every
  // `process.env.NODE_ENV` reference in app code at BUILD time — the
  // `.env.${process.env.NODE_ENV || "development"}` template literal
  // compiled down to the bare string `.env.production` with the
  // conditional expression entirely erased, regardless of what the real
  // container's NODE_ENV actually is at runtime (verified: built with
  // NODE_ENV=production forced, ran with NODE_ENV=development override,
  // output still read "production" — there is nothing left at runtime to
  // read). NODE_ENV genuinely cannot carry a runtime signal in Vite-
  // bundled code, so infra/copilot-service/service.ts injects this
  // stack's value under a separate, non-Vite-reserved name instead. This
  // function itself runs server-side at real Node boot (not bundled into
  // app code), so process.env.NODE_ENV below is the genuine runtime value.
  //
  // .env.local / .env.{DEPLOY_ENV}.local (last, highest priority) reproduce
  // the one piece of dotenvx's `convention: "nextjs"` worth keeping after
  // moving off it: documented, gitignored, developer-local overrides (see
  // apps/api/src/config.ts's identical addition). Skipped for NODE_ENV=test,
  // same as the convention did.
  const deployEnv = process.env.DEPLOY_ENV || "development";
  dotenv.config({
    path: [
      ".env",
      `.env.${deployEnv}`,
      ...(process.env.NODE_ENV !== "test" ? [".env.local"] : []),
      `.env.${deployEnv}.local`,
    ],
    envKeysFile: "../../.env.keys",
    quiet: true,
    overload: true,
  });
  return fromProcessEnv();
});

export const config: PublicConfig =
  typeof window === "undefined"
    ? fromDecryptedEnvFile()
    : window.__FLOWSTEP_CONFIG__!;
